import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { WizValidators } from '@commons/validators';
import { LocationFieldConfig } from '@models/location/location-input';
import { PoleEmploiCity } from '@models/pole-emploi/city';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchCommunesWebservice } from '@webservices/search/search-communes.webservice';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

export const DEFAULT_ZIPCODE_CONFIG: LocationFieldConfig = {
  label: 'Code postal',
  error: 'Veuillez saisir votre code postal',
  emptyData: "Vérifiez qu'il s'agisse bien d'un code postal ou qu'il ne comporte pas de faute.",
  invalid: "Le code postal n'est pas valide",
  required: false,
  ariaLabel: 'Code postal',
};

@UntilDestroy()
@Component({
  selector: 'app-zipcode-input-pole-emploi',
  templateUrl: './zipcode-input-pole-emploi.component.html',
  styleUrls: ['./zipcode-input-pole-emploi.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZipcodeInputPoleEmploiComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZipcodeInputPoleEmploiComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private _fieldConfig = DEFAULT_ZIPCODE_CONFIG;

  control = new FormControl(undefined, [WizValidators.cityPoleEmploiRequired]);
  @Input() set fieldConfig(value: LocationFieldConfig) {
    this._fieldConfig = value;

    this.control.setValidators([
      ...(this._fieldConfig.required ? [Validators.required] : []),
      WizValidators.cityPoleEmploiRequired,
    ]);

    this.control.updateValueAndValidity();
  }

  get fieldConfig(): LocationFieldConfig {
    return this._fieldConfig;
  }

  private _value?: PoleEmploiCity;

  set value(val: PoleEmploiCity | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): PoleEmploiCity | undefined {
    return this.value;
  }

  cities$ = new Observable<PoleEmploiCity[]>();
  emptyDataError$!: Observable<boolean>;

  formSubmitSubscription?: Subscription;

  constructor(
    private readonly searchCommunesWebservice: SearchCommunesWebservice,
    private readonly cdr: ChangeDetectorRef,
    private readonly controlContainer: ControlContainer
  ) {
    if (this.controlContainer?.formDirective) {
      this.formSubmitSubscription = (controlContainer.formDirective as FormGroupDirective).ngSubmit.subscribe({
        next: () => {
          this.control.markAllAsTouched();
          this.control.updateValueAndValidity();
        },
      });
    }
  }

  onChange: (val?: PoleEmploiCity) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val: PoleEmploiCity): void {
    this.control.setValue(val);
  }

  registerOnChange(fn: (val?: PoleEmploiCity) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  doClosed(): void {
    if (!this.fieldConfig.required || (!this.control?.errors?.cityRequired && !this.control?.errors?.required)) {
      return;
    }

    this.cities$
      .pipe(
        take(1),
        map((cities) => cities[0]),
        filter((city) => !!city)
      )
      .subscribe({
        next: (city) => {
          this.control.setValue(city);
          this.control.updateValueAndValidity();
        },
      });
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (val) => {
        if (typeof val === 'object') {
          this.value = val;
        }
      },
    });

    this.cities$ = this.control.valueChanges.pipe(
      untilDestroyed(this),
      debounceTime(300),
      filter((query) => query && query.length > 0),
      distinctUntilChanged(),
      switchMap((query) => this.searchCommunesWebservice.getCommunes(query).pipe(map((res) => res.hits))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.emptyDataError$ = this.cities$.pipe(map((cities) => cities?.length === 0));
  }

  trackByCity = (_: number, city: PoleEmploiCity): string => city.code;
  displayCity = (city?: PoleEmploiCity): string => (city?.codePostal ? `${city?.codePostal} - ${city?.libelle}` : '');

  ngOnDestroy(): void {
    if (this.formSubmitSubscription) {
      this.formSubmitSubscription.unsubscribe();
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [ZipcodeInputPoleEmploiComponent],
  exports: [ZipcodeInputPoleEmploiComponent],
})
export class ZipcodeInputPoleEmploiModule {}
